import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import {PageHeader} from "../../../components/PageHeader";
import CodeManageSettingsLayout from "../fieldMng/shared/CodeManageSettingsLayout";
import CodeManageItemList from "../fieldMng/shared/CodeManageItemList";
import {
    filterCategoriesByPlatform,
    pickActiveCategoryId,
} from "../groupCode/menuPlatformFilter";
import {groupByGroupName, isEmpty} from "../../../components/commonmethod/CommonMethod";
import {handleActiveToast} from "../../../components/sonner/ToastBasic";
import {CacheType} from "../../../constant/CodeSpecNum";
import {useCacheRefresh} from "../../../hooks/useCacheRefresh";
import {
    getInspectionReportCode,
    removeInspectionReportCodes,
    saveInspectionReportCode,
    updateInspectionReportCode,
} from "../../../api/inspectionReportCode/InspectionReportCodeService";
import {
    getDailyCheck,
    removeDailyChecks,
    saveDailyCheck,
    updateDailyCheck,
} from "../../../api/dailyCheck/DailyCheckService";
import {getApprovalProcessById} from "../../../api/approval/ApprovalProcessService";
import InspectionReportCodeEditorPanel from "./InspectionReportCodeEditorPanel";
import DailyCheckCodeEditorPanel from "./DailyCheckCodeEditorPanel";
import {
    parseReportCodeRoute,
    REPORT_CODE_CATEGORIES,
    REPORT_CODE_ROUTE_CATEGORY,
} from "./reportCodeCategoryConfig";
import {useReportCodeList} from "./useReportCodeList";
import "../userAccount/userAccountEditForm.css";
import "../fieldMng/shared/unifiedCodeManage.css";
import "../fieldMng/shared/codeManageSettings.css";
import "../groupCode/groupCodeManage.css";
import "./reportCodeManage.css";

function createInspectionDraft(name = "") {
    return {
        _isNew: true,
        reportCode: "code",
        name,
        etc: "",
        required_validate: {reportCode: true},
    };
}

function createDailyCheckDraft(name = "") {
    return {
        _isNew: true,
        name,
        etc: "",
    };
}

function ReportCodeManage() {
    const location = useLocation();
    const platFrom = useSelector((state) => state.auth.platFrom);
    const approvalProcess = useSelector((state) => state.content.approvalProcess);
    const refreshCache = useCacheRefresh();

    const visibleCategories = useMemo(
        () => filterCategoriesByPlatform(REPORT_CODE_CATEGORIES, platFrom),
        [platFrom],
    );

    const [activeCategory, setActiveCategory] = useState(() =>
        pickActiveCategoryId(
            REPORT_CODE_CATEGORIES,
            REPORT_CODE_ROUTE_CATEGORY[location.pathname]
                ?? parseReportCodeRoute(location.pathname).categoryId
                ?? "inspectionReport",
            platFrom,
        ),
    );

    const activeMeta = useMemo(
        () => visibleCategories.find((item) => item.id === activeCategory)
            ?? visibleCategories[0]
            ?? null,
        [activeCategory, visibleCategories],
    );

    const {listData, loading, reload} = useReportCodeList(activeCategory);
    const [selectedItem, setSelectedItem] = useState(null);
    const [children, setChildren] = useState([]);
    const [viewMode, setViewMode] = useState("browse");

    const loadInspectionDetail = useCallback(async (id) => {
        const response = await getInspectionReportCode(id);
        const data = response.data;
        setSelectedItem({
            ...data,
            reportCodeKey: {id: data.reportCodeKey, name: data.reportCodeText},
        });
        setChildren(data.children ? groupByGroupName(data.children) : []);
    }, []);

    const loadDailyCheckDetail = useCallback(async (id) => {
        const response = await getDailyCheck(id);
        const data = response.data;
        let approvalProcessName = data.approvalProcess?.name;

        if (!approvalProcessName && data.approvalProcessId) {
            const approvalResponse = await getApprovalProcessById(data.approvalProcessId);
            approvalProcessName = approvalResponse.data?.name;
        }

        setSelectedItem({
            ...data,
            approvalProcessName,
        });
        setChildren(data.children ?? []);
    }, []);

    const applyRouteState = useCallback(async () => {
        const route = parseReportCodeRoute(location.pathname);
        const categoryId = REPORT_CODE_ROUTE_CATEGORY[location.pathname] ?? route.categoryId;

        if (categoryId) {
            const nextId = pickActiveCategoryId(REPORT_CODE_CATEGORIES, categoryId, platFrom);
            if (nextId) {
                setActiveCategory(nextId);
            }
        }

        if (route.isCreate) {
            setSelectedItem(
                route.categoryId === "dailyCheck"
                    ? createDailyCheckDraft()
                    : createInspectionDraft(),
            );
            setChildren([]);
            setViewMode("create");
            return;
        }

        if (route.itemId) {
            if (route.categoryId === "dailyCheck") {
                await loadDailyCheckDetail(route.itemId);
            } else {
                await loadInspectionDetail(route.itemId);
            }
            setViewMode("browse");
            return;
        }

        setSelectedItem(null);
        setChildren([]);
        setViewMode("browse");
    }, [loadDailyCheckDetail, loadInspectionDetail, location.pathname, platFrom]);

    useEffect(() => {
        applyRouteState();
    }, [applyRouteState]);

    const handleCategoryChange = (categoryId) => {
        setActiveCategory(categoryId);
        setSelectedItem(null);
        setChildren([]);
        setViewMode("browse");
    };

    const handleSelect = async (item) => {
        if (activeCategory === "dailyCheck") {
            await loadDailyCheckDetail(item.id);
        } else {
            await loadInspectionDetail(item.id);
        }
    };

    const handleCancelCreate = () => {
        setSelectedItem(null);
        setChildren([]);
        setViewMode("browse");
    };

    const handleStartCreate = () => {
        if (activeCategory === "dailyCheck") {
            setSelectedItem(createDailyCheckDraft());
        } else {
            setSelectedItem(createInspectionDraft());
        }
        setChildren([]);
        setViewMode("create");
    };

    const validateInspection = () => {
        const requireds = ["reportCode", "reportCodeKey", "name"];
        if (requireds.some((field) => isEmpty(selectedItem[field]))) {
            alert("필수값이 누락되었습니다.");
            return false;
        }
        if (!selectedItem.reportCode || !selectedItem.reportCodeKey) {
            alert("코드구분 혹은 코드 타입을 지정해주세요");
            return false;
        }
        if (children.length === 0) {
            alert("점검보고서 항목은 필수입니다.");
            return false;
        }

        const hasEmptyGroupName = children.some((group) => !group.groupName?.trim());
        if (hasEmptyGroupName) {
            alert("그룹명을 입력해주세요.");
            return false;
        }

        const groupNames = children.map((group) => group.groupName.trim());
        if (groupNames.length !== new Set(groupNames).size) {
            alert("이미 존재하는 그룹명입니다.");
            return false;
        }

        const flattenedList = [];
        children.forEach((group) => {
            group.child.forEach((child) => flattenedList.push(child));
        });

        const hasEmptyName = flattenedList.some((each) => {
            if (isEmpty(each.name)) {
                alert("점검보고서 관리항목 명을 작성해주세요");
                return true;
            }
            return false;
        });

        return !hasEmptyName;
    };

    const validateDailyCheck = () => {
        const hasEmptyName = children.some((each) => {
            if (isEmpty(each.name)) {
                alert("일일체크코드 관리항목 명을 작성해주세요");
                return true;
            }
            return false;
        });
        return !hasEmptyName;
    };

    const handleSaveInspection = async () => {
        if (!validateInspection()) {
            return;
        }

        const flattenedList = [];
        children.forEach((group) => {
            group.child.forEach((child) => flattenedList.push(child));
        });

        const basePayload = {
            ...selectedItem,
            children: flattenedList,
            reportCodeKey: selectedItem.reportCodeKey.id,
            reportCodeText: selectedItem.reportCodeKey.name,
        };
        delete basePayload._isNew;
        delete basePayload.groupName;

        const payload = selectedItem._isNew
            ? basePayload
            : (() => {
                const {temp, createdAt, reportCodeKey: _key, ...rest} = basePayload;
                return rest;
            })();

        const response = selectedItem._isNew
            ? await saveInspectionReportCode(payload)
            : await updateInspectionReportCode(payload);

        if (response.status === 200) {
            handleActiveToast({message: selectedItem._isNew ? "등록되었습니다." : "수정되었습니다."});
            refreshCache(CacheType.inspectionReportAsset);
            refreshCache(CacheType.inspectionReportCompany);
            const list = await reload();
            if (selectedItem._isNew && list?.length) {
                const created = list.find((item) => item.name === payload.name) ?? list[0];
                setViewMode("browse");
                await loadInspectionDetail(created.id);
            } else if (!selectedItem._isNew) {
                await loadInspectionDetail(selectedItem.id);
            }
        }
    };

    const handleSaveDailyCheck = async () => {
        if (!selectedItem.name?.trim()) {
            alert("일일체크코드 명을 입력해주세요.");
            return;
        }
        if (!validateDailyCheck()) {
            return;
        }

        const payload = {
            ...selectedItem,
            children,
            approvalProcessId: approvalProcess?.id ?? selectedItem.approvalProcessId ?? null,
        };
        delete payload._isNew;
        delete payload.approvalProcessName;

        const response = selectedItem._isNew
            ? await saveDailyCheck(payload)
            : await updateDailyCheck(payload);

        if (response.status === 200) {
            handleActiveToast({message: selectedItem._isNew ? "등록되었습니다." : "수정되었습니다."});
            refreshCache(CacheType.dailyCheck);
            const list = await reload();
            if (selectedItem._isNew && list?.length) {
                const created = list.find((item) => item.name === payload.name) ?? list[0];
                setViewMode("browse");
                await loadDailyCheckDetail(created.id);
            } else if (!selectedItem._isNew) {
                await loadDailyCheckDetail(selectedItem.id);
            }
        }
    };

    const handleDelete = async () => {
        if (!selectedItem?.id || !window.confirm("선택한 코드를 삭제하시겠습니까?")) {
            return;
        }

        if (activeCategory === "dailyCheck") {
            const response = await removeDailyChecks({ids: [selectedItem.id]});
            if (response.status === 200) {
                handleActiveToast({message: "삭제되었습니다."});
                refreshCache(CacheType.dailyCheck);
                setSelectedItem(null);
                setChildren([]);
                await reload();
            }
            return;
        }

        const response = await removeInspectionReportCodes({ids: [selectedItem.id]});
        if (response.status === 200) {
            handleActiveToast({message: "삭제되었습니다."});
            refreshCache(CacheType.inspectionReportAsset);
            refreshCache(CacheType.inspectionReportCompany);
            setSelectedItem(null);
            setChildren([]);
            await reload();
        }
    };

    if (!visibleCategories.length) {
        return (
            <div className="common-container container-height-full">
                <PageHeader
                    header="보고서/기타 코드"
                    indicator="환경설정 > 보고서/기타"
                />
                <p className="group-code-manage__empty-platform">
                    현재 플랫폼({platFrom ?? "-"})에서 사용할 수 있는 보고서 코드 항목이 없습니다.
                </p>
            </div>
        );
    }

    if (!activeMeta) {
        return null;
    }

    const readOnly = selectedItem?.useCount > 0;
    const isCreateMode = viewMode === "create" && selectedItem?._isNew;

    const panelTitle = isCreateMode
        ? (activeCategory === "dailyCheck" ? "일일체크코드 등록" : "점검보고서 등록")
        : activeMeta.title;

    const panelDescription = isCreateMode
        ? (activeCategory === "dailyCheck"
            ? "새 일일체크코드와 관리 항목을 등록합니다."
            : "새 점검보고서 코드와 관리 항목을 등록합니다.")
        : activeMeta.description;

    const panelHeadActions = isCreateMode ? (
        <>
            <button
                type="button"
                className="code-manage-neutral-btn"
                onClick={handleCancelCreate}
            >
                취소
            </button>
            <button
                type="button"
                className="save-btn"
                onClick={activeCategory === "dailyCheck" ? handleSaveDailyCheck : handleSaveInspection}
            >
                등록
            </button>
        </>
    ) : (
        <button
            type="button"
            className="save-btn"
            onClick={handleStartCreate}
        >
            등록
        </button>
    );

    const editorPanel = activeCategory === "dailyCheck" ? (
        <DailyCheckCodeEditorPanel
            item={selectedItem}
            setItem={setSelectedItem}
            children={children}
            setChildren={setChildren}
            readOnly={readOnly}
            onSave={handleSaveDailyCheck}
            onDelete={handleDelete}
            hideFormActions={isCreateMode}
        />
    ) : (
        <InspectionReportCodeEditorPanel
            item={selectedItem}
            setItem={setSelectedItem}
            children={children}
            setChildren={setChildren}
            readOnly={readOnly}
            onSave={handleSaveInspection}
            onDelete={handleDelete}
            hideFormActions={isCreateMode}
        />
    );

    return (
        <div className="common-container container-height-full">
            <PageHeader
                header="보고서/기타 코드"
                indicator="환경설정 > 보고서/기타"
            />

            <CodeManageSettingsLayout
                navItems={visibleCategories}
                activeCategory={activeCategory}
                onCategoryChange={handleCategoryChange}
                panelTitle={panelTitle}
                panelDescription={panelDescription}
                panelHeadActions={panelHeadActions}
                fullPanel={isCreateMode ? (
                    <div className="code-manage-panel__body report-code-manage-panel">
                        <div className="code-manage-panel__editor code-manage-scope report-code-manage-panel__editor--full">
                            {editorPanel}
                        </div>
                    </div>
                ) : null}
                list={!isCreateMode ? (
                    <CodeManageItemList
                        addLabel={activeMeta.addLabel}
                        items={listData}
                        selectedItemId={selectedItem?.id}
                        onSelect={handleSelect}
                        showCreateSection={false}
                        emptyMessage={loading ? "목록을 불러오는 중입니다." : "등록된 코드가 없습니다. 상단 등록 버튼을 눌러 추가하세요."}
                    />
                ) : null}
                editor={!isCreateMode ? editorPanel : null}
            />
        </div>
    );
}

export default ReportCodeManage;
